import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { PostFormAction } from '../../Component/PostFormAction'
import { mediaUrl } from '../../utils/mediaUrl'
import { registerQuillTable } from './quillTable'

registerQuillTable()

const BlockEmbed = Quill.import('blots/block/embed')
const Delta = Quill.import('delta')
const Font = Quill.import('formats/font')
const Size = Quill.import('attributors/style/size')

Font.whitelist = ['sans-serif', 'serif', 'monospace']
Quill.register(Font, true)

Size.whitelist = ['10px', '12px', '14px', '16px', '18px', '24px', '32px']
Quill.register(Size, true)

class MapEmbedBlot extends BlockEmbed {
  static create(value) {
    const node = super.create()
    node.classList.add('ql-google-map')
    const iframe = document.createElement('iframe')
    iframe.setAttribute('src', String(value || ''))
    iframe.setAttribute('title', 'Google Map')
    iframe.setAttribute('loading', 'lazy')
    iframe.setAttribute('allowfullscreen', 'true')
    iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade')
    iframe.setAttribute('frameborder', '0')
    iframe.style.border = '0'
    iframe.style.width = '100%'
    iframe.style.minHeight = '360px'
    node.appendChild(iframe)
    return node
  }

  static value(node) {
    const iframe = node.querySelector('iframe')
    return iframe ? iframe.getAttribute('src') || '' : ''
  }
}

MapEmbedBlot.blotName = 'mapEmbed'
MapEmbedBlot.tagName = 'DIV'
MapEmbedBlot.className = 'ql-google-map'
Quill.register('formats/mapEmbed', MapEmbedBlot, true)

class DividerBlot extends BlockEmbed {
  static create() {
    const node = super.create()
    node.setAttribute('contenteditable', 'false')
    return node
  }

  static value() {
    return true
  }
}

DividerBlot.blotName = 'divider'
DividerBlot.tagName = 'HR'
Quill.register(DividerBlot)

const icons = Quill.import('ui/icons')
icons.mapEmbed =
  '<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path class="ql-fill" d="M9 1.2A4.8 4.8 0 0 0 4.2 6c0 3.6 4.8 10.2 4.8 10.2S13.8 9.6 13.8 6A4.8 4.8 0 0 0 9 1.2zm0 6.9A2.1 2.1 0 1 1 9 3.9a2.1 2.1 0 0 1 0 4.2z"/></svg>'
icons.divider =
  '<svg viewBox="0 0 18 18"><line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line></svg>'
icons.undo =
  '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="3.5 8 7 11.5 3.5 15"/><path class="ql-stroke" d="M15,5.5A5.5,5.5,0,0,0,4,9"/></svg>'
icons.redo =
  '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="14.5 8 11 11.5 14.5 15"/><path class="ql-stroke" d="M3,5.5A5.5,5.5,0,0,1,14,9"/></svg>'
icons.source =
  '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="5 6 2 9 5 12"/><polyline class="ql-stroke" points="13 6 16 9 13 12"/><line class="ql-stroke" x1="10" x2="8" y1="4" y2="14"/></svg>'
icons.fullscreen =
  '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="3 7 3 3 7 3"/><polyline class="ql-stroke" points="15 7 15 3 11 3"/><polyline class="ql-stroke" points="3 11 3 15 7 15"/><polyline class="ql-stroke" points="15 11 15 15 11 15"/></svg>'
icons.imageUrl =
  '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect><circle class="ql-fill" cx="6" cy="7" r="1"></circle><polyline class="ql-stroke" points="5 12 8 9 11 12 13 10 15 12"/></svg>'

function isAllowedMapSrc(src) {
  try {
    const url = new URL(src)
    if (url.protocol !== 'https:') return false
    const host = url.hostname.toLowerCase()
    const googleHost =
      host === 'google.com' ||
      host.endsWith('.google.com') ||
      /^google\.[a-z.]+$/.test(host) ||
      /\.google\.[a-z.]+$/.test(host)
    if (!googleHost) return false
    return (
      url.pathname.includes('/maps/embed') ||
      url.pathname.includes('/maps/d/embed') ||
      url.searchParams.get('output') === 'embed'
    )
  } catch (e) {
    return false
  }
}

function extractMapSrc(raw) {
  const text = String(raw || '').trim()
  if (!text) return ''
  const iframeMatch = text.match(/src\s*=\s*["']([^"']+)["']/i)
  const candidate = iframeMatch ? iframeMatch[1].trim() : text
  return isAllowedMapSrc(candidate) ? candidate : ''
}

function toEmbedVideoUrl(raw) {
  const text = String(raw || '').trim()
  if (!text) return ''
  const iframeMatch = text.match(/src\s*=\s*["']([^"']+)["']/i)
  const candidate = iframeMatch ? iframeMatch[1].trim() : text
  try {
    const url = new URL(candidate)
    if (url.protocol !== 'https:') return ''
    const host = url.hostname.replace(/^www\./, '').toLowerCase()
    if (host === 'youtu.be') {
      const id = url.pathname.replace(/^\//, '')
      return id ? `https://www.youtube.com/embed/${id}` : ''
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = url.searchParams.get('v')
      if (id) return `https://www.youtube.com/embed/${id}`
      if (url.pathname.startsWith('/embed/')) return `https://www.youtube.com${url.pathname}`
    }
    if (host === 'vimeo.com') {
      const id = url.pathname.split('/').filter(Boolean)[0]
      return id ? `https://player.vimeo.com/video/${id}` : ''
    }
    if (host === 'player.vimeo.com' && url.pathname.includes('/video/')) {
      return candidate
    }
    return ''
  } catch (e) {
    return ''
  }
}

function matchGoogleMapIframe(node, delta) {
  const parent = node.parentNode
  if (parent && parent.classList && parent.classList.contains('ql-google-map')) {
    return new Delta()
  }
  const src = extractMapSrc(node.getAttribute('src') || '')
  if (!src) return delta
  return new Delta().insert({ mapEmbed: src })
}

function matchGoogleMapWrap(node, delta) {
  if (!node.classList || !node.classList.contains('ql-google-map')) {
    return delta
  }
  const src = extractMapSrc(node.innerHTML) || MapEmbedBlot.value(node)
  if (!src) return delta
  return new Delta().insert({ mapEmbed: src })
}

const DEFAULT_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'script',
  'list',
  'bullet',
  'indent',
  'align',
  'direction',
  'blockquote',
  'code-block',
  'link',
  'image',
  'video',
  'mapEmbed',
  'divider',
  'table',
]

function insertAtSelection(quill, embedName, value) {
  const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 }
  quill.insertEmbed(range.index, embedName, value, 'user')
  quill.setSelection(range.index + 1, 0, 'user')
}

function parseTableCount(raw) {
  const n = parseInt(String(raw || '').trim(), 10)
  if (!Number.isFinite(n) || n < 1) return 0
  return Math.min(12, n)
}

function promptTableSize() {
  const rowsRaw = window.prompt('Number of rows (1–12):', '3')
  if (rowsRaw == null) return null
  const colsRaw = window.prompt('Number of columns (1–12):', '3')
  if (colsRaw == null) return null
  const rows = parseTableCount(rowsRaw)
  const cols = parseTableCount(colsRaw)
  if (!rows || !cols) {
    window.alert('Please enter valid row and column numbers.')
    return null
  }
  return { rows, cols }
}

function resetTablePicker(toolbarId) {
  const select = document.querySelector(`#${toolbarId} select.ql-table`)
  if (select) {
    select.value = ''
    select.selectedIndex = 0
  }
}

/**
 * Expanded Quill CMS editor — formatting, media, tables, map, source, fullscreen.
 */
function RichTextEditor({ value, onChange, id, placeholder }) {
  const toolbarId = id ? `${id}-toolbar` : 'manage-rte-toolbar'
  const wrapRef = useRef(null)
  const quillRef = useRef(null)
  const fileInputRef = useRef(null)
  const pendingQuillRef = useRef(null)
  const [sourceMode, setSourceMode] = useState(false)
  const [sourceHtml, setSourceHtml] = useState('')
  const [fullscreen, setFullscreen] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  const uploadAndInsertImage = async (file, quill) => {
    if (!file || !quill) return
    if (!/^image\/(jpeg|png|gif|webp)$/i.test(file.type)) {
      window.alert('Please choose a JPG, PNG, GIF, or WEBP image.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      window.alert('Image must be 5 MB or smaller.')
      return
    }

    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('image', file)
      const data = await PostFormAction('post', fd, 'pages/uploadImage')
      const payload = data?.response ?? data?.data?.response
      const rawPath =
        (typeof payload === 'string' ? payload : null) ||
        payload?.url ||
        payload?.path ||
        ''
      const src = mediaUrl(rawPath)
      if (data?.status !== 'success' || !src) {
        window.alert(
          data?.message ||
            data?.data?.message ||
            'Image upload failed. Please try again.'
        )
        return
      }
      insertAtSelection(quill, 'image', src)
    } finally {
      setUploading(false)
    }
  }

  const onImageFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const quill = pendingQuillRef.current || quillRef.current?.getEditor?.()
    pendingQuillRef.current = null
    if (!file) return
    await uploadAndInsertImage(file, quill)
  }

  const modules = useMemo(
    () => ({
      history: { delay: 500, maxStack: 200, userOnly: true },
      table: true,
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          undo() {
            this.quill.history.undo()
          },
          redo() {
            this.quill.history.redo()
          },
          mapEmbed() {
            const input = window.prompt(
              'Paste the Google Maps embed code (iframe) or the maps embed URL:'
            )
            if (input == null) return
            const src = extractMapSrc(input)
            if (!src) {
              window.alert(
                'That is not a valid Google Maps embed. In Google Maps, use Share → Embed a map, then copy the iframe code.'
              )
              return
            }
            insertAtSelection(this.quill, 'mapEmbed', src)
          },
          divider() {
            insertAtSelection(this.quill, 'divider', true)
          },
          table(value) {
            const table = this.quill.getModule('table')
            resetTablePicker(toolbarId)
            if (!table || !value) return
            if (value === 'new') {
              const size = promptTableSize()
              if (size) table.insertTable(size.rows, size.cols)
              return
            }
            const [inside] = table.getTable()
            if (!inside) {
              window.alert('Place the cursor inside a table first.')
              return
            }
            if (value === 'row') table.insertRowBelow()
            else if (value === 'col') table.insertColumnRight()
            else if (value === 'del-row') table.deleteRow()
            else if (value === 'del-col') table.deleteColumn()
            else if (value === 'del-table') table.deleteTable()
          },
          image() {
            pendingQuillRef.current = this.quill
            if (fileInputRef.current) {
              fileInputRef.current.click()
            }
          },
          imageUrl() {
            const input = window.prompt('Paste image URL (https://…):')
            if (input == null) return
            const url = String(input).trim()
            if (!/^https?:\/\//i.test(url)) {
              window.alert(
                'Please enter a valid image URL starting with http:// or https://'
              )
              return
            }
            insertAtSelection(this.quill, 'image', url)
          },
          video() {
            const input = window.prompt(
              'Paste YouTube/Vimeo URL or embed iframe code:'
            )
            if (input == null) return
            const src = toEmbedVideoUrl(input)
            if (!src) {
              window.alert('Please paste a valid YouTube or Vimeo link.')
              return
            }
            insertAtSelection(this.quill, 'video', src)
          },
          source() {
            const quill = this.quill
            const html = quill.root.innerHTML
            setSourceHtml(html === '<p><br></p>' ? '' : html)
            setSourceMode(true)
          },
          fullscreen() {
            setFullscreen((v) => !v)
          },
        },
      },
      clipboard: {
        matchers: [
          ['iframe', matchGoogleMapIframe],
          ['div', matchGoogleMapWrap],
        ],
      },
    }),
    [toolbarId]
  )

  const formats = useMemo(() => DEFAULT_FORMATS, [])

  const applySource = () => {
    setSourceMode(false)
    if (onChange) onChange(sourceHtml || '')
  }

  const cancelSource = () => {
    setSourceMode(false)
  }

  return (
    <div
      ref={wrapRef}
      className={`manage-rte${fullscreen ? ' manage-rte-fullscreen' : ''}${
        uploading ? ' manage-rte-uploading' : ''
      }`}
      id={id ? `${id}-wrap` : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
        style={{ display: 'none' }}
        onChange={onImageFileChange}
      />
      {uploading ? (
        <div className="manage-rte-upload-banner" role="status">
          Uploading image…
        </div>
      ) : null}
      {fullscreen ? (
        <div className="manage-rte-fs-bar">
          <span>Full screen editor</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setFullscreen(false)}
          >
            Exit (Esc)
          </button>
        </div>
      ) : null}

      <div id={toolbarId} className="ql-toolbar ql-snow manage-rte-toolbar">
        <span className="ql-formats">
          <button type="button" className="ql-undo" title="Undo" />
          <button type="button" className="ql-redo" title="Redo" />
        </span>
        <span className="ql-formats">
          <select className="ql-header" defaultValue="">
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="">Normal</option>
          </select>
          <select className="ql-font" defaultValue="">
            <option value="">Sans Serif</option>
            <option value="serif">Serif</option>
            <option value="monospace">Monospace</option>
          </select>
          <select className="ql-size" defaultValue="">
            <option value="10px">10</option>
            <option value="12px">12</option>
            <option value="14px">14</option>
            <option value="">16</option>
            <option value="18px">18</option>
            <option value="24px">24</option>
            <option value="32px">32</option>
          </select>
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-bold" />
          <button type="button" className="ql-italic" />
          <button type="button" className="ql-underline" />
          <button type="button" className="ql-strike" />
          <select className="ql-color" title="Text color" />
          <select className="ql-background" title="Highlight" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-script" value="super" title="Superscript" />
          <button type="button" className="ql-script" value="sub" title="Subscript" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-list" value="ordered" />
          <button type="button" className="ql-list" value="bullet" />
          <button type="button" className="ql-indent" value="-1" title="Decrease indent" />
          <button type="button" className="ql-indent" value="+1" title="Increase indent" />
        </span>
        <span className="ql-formats">
          <select className="ql-align" defaultValue="" />
          <button type="button" className="ql-direction" value="rtl" title="Text direction" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-blockquote" title="Quote" />
          <button type="button" className="ql-code-block" title="Code block" />
          <button type="button" className="ql-link" title="Link" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-image" title="Upload image" />
          <button type="button" className="ql-imageUrl" title="Insert image from URL" />
          <button type="button" className="ql-video" title="Insert video" />
          <button
            type="button"
            className="ql-mapEmbed"
            title="Insert Google Map"
            aria-label="Insert Google Map"
          />
          <button type="button" className="ql-divider" title="Horizontal line" />
          <select className="ql-table" defaultValue="" title="Table">
            <option value="" />
            <option value="new">Insert table</option>
            <option value="row">Insert row below</option>
            <option value="col">Insert column right</option>
            <option value="del-row">Delete row</option>
            <option value="del-col">Delete column</option>
            <option value="del-table">Delete table</option>
          </select>
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-clean" title="Clear formatting" />
          <button type="button" className="ql-source" title="HTML source" />
          <button type="button" className="ql-fullscreen" title="Full screen" />
        </span>
      </div>

      {sourceMode ? (
        <div className="manage-rte-source">
          <textarea
            className="form-control manage-rte-source-area"
            value={sourceHtml}
            onChange={(e) => setSourceHtml(e.target.value)}
            spellCheck={false}
          />
          <div className="manage-rte-source-actions">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              onClick={cancelSource}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={applySource}>
              Apply HTML
            </button>
          </div>
        </div>
      ) : (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder || 'Write page content…'}
        />
      )}
    </div>
  )
}

export default RichTextEditor
